import React from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap'

const SalesUsersDetailForm = ({
  detail,
  bankVerification,
  index,
  displayNumber,
  canRemove,
  onChange,
  onMakePrimary,
  onRemove,
  onUndoRemove,
  onSave,
  saving,

  // ✅ upload props
  uploadingIdCard,
  onUploadIdCard,
  onRemoveIdCard,
}) => {
  const removed = String(detail?._destroy || '0') === '1'
  const set = (key, value) => onChange(index, { ...detail, [key]: value })

  const idCardMeta = detail?.id_card || null
  const idCardUrl = detail?.id_card_local_preview_url || idCardMeta?.url || ''
  const idCardType = (idCardMeta?.content_type || '').toLowerCase()

  // local preview URL hanya dibuat untuk image di SalesUsersForm
  const hasLocalPreview = !!detail?.id_card_local_preview_url
  const isImage = hasLocalPreview || idCardType.startsWith('image/')
  const isPdf = !hasLocalPreview && idCardType === 'application/pdf'

  const handleIdCardChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    onUploadIdCard(index, file)
    e.target.value = '' // allow pick same file again
  }

  return (
    <Card className="mt-3" style={{ opacity: removed ? 0.6 : 1 }}>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div>
          <b>{detail?.label?.trim() ? detail.label : `Detail #${displayNumber}`}</b>
          {detail?.id ? <span className="ml-2 text-muted">ID: {detail.id}</span> : null}
          {removed ? <span className="ml-2 text-danger">(removed)</span> : null}
        </div>

        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          {!removed && canRemove ? (
            <Button size="sm" color="danger" onClick={() => onRemove(index)}>
              Remove
            </Button>
          ) : null}

          {removed ? (
            <Button size="sm" color="secondary" onClick={() => onUndoRemove(index)}>
              Undo
            </Button>
          ) : null}
        </div>
      </CardHeader>

      <CardBody>
        {/* Primary */}
        <FormGroup check className="mb-3">
          <Label check>
            <Input
              type="radio"
              name="sales-user-primary-detail"
              checked={!!detail.primary}
              disabled={removed}
              onChange={() => onMakePrimary(index)}
            />{' '}
            Primary Detail
          </Label>
        </FormGroup>

        {/* Basic info */}
        <Row form>
          <Col md="6">
            <FormGroup>
              <Label>Label (email non-primary, buyer/ seller/ consignor, etc)</Label>
              <Input
                disabled={removed}
                value={detail.label || ''}
                onChange={(e) => set('label', e.target.value)}
                placeholder="e.g. Ardine (buyer)"
              />
            </FormGroup>
          </Col>

          <Col md="6">
            <FormGroup>
              <Label>Name</Label>
              <Input
                disabled={removed}
                value={detail.name || ''}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Full name"
              />
            </FormGroup>
          </Col>
        </Row>

        <Row form>
          <Col md="6">
            <FormGroup>
              <Label>Phone Code</Label>
              <Input
                disabled={removed}
                value={detail.phone_code_id ?? ''}
                onChange={(e) => set('phone_code_id', e.target.value ? Number(e.target.value) : null)}
                placeholder="62"
              />
            </FormGroup>
          </Col>

          <Col md="6">
            <FormGroup>
              <Label>Phone (digits only)</Label>
              <Input
                disabled={removed}
                value={detail.phone || ''}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="8123456789"
              />
            </FormGroup>
          </Col>
        </Row>

        {/* KTP Upload */}
        <div className="border rounded p-3 mb-3" id={`user-detail-evidence-${detail.id || index}`} data-primary-evidence={detail.primary ? 'true' : 'false'}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">KTP / bukti chat validitas nomor dan nama rekening</h5>
            {idCardMeta?.filename ? (
              <span className="text-muted">Current: {idCardMeta.filename}</span>
            ) : null}
          </div>

          <Row form>
            <Col md="6">
              <FormGroup className="mb-2">
                <Label>Upload KTP seller jika nama pemilik rekening berbeda (JPG/JPEG/PNG/PDF, max 5MB). Bukti chat validitas nomor dan nama rekening diunggah pada Bank Account Section.</Label>
                <Input
                  type="file"
                  disabled={removed || uploadingIdCard}
                  accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handleIdCardChange}
                />
                {uploadingIdCard ? <div className="text-muted mt-2">Uploading...</div> : null}
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup className="mb-0">
                <Label>Preview</Label>

                <div className="border rounded p-2" style={{ minHeight: 90 }}>
                  {idCardUrl && isImage ? (
                    <img
                      src={idCardUrl}
                      alt="KTP Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 180,
                        borderRadius: 8,
                        border: '1px solid #eee',
                      }}
                    />
                  ) : null}

                  {idCardUrl && isPdf ? (
                    <a href={idCardUrl} target="_blank" rel="noreferrer">
                      View PDF
                    </a>
                  ) : null}

                  {!idCardUrl ? <div className="text-muted">No document uploaded.</div> : null}
                </div>

                {!removed && (idCardMeta?.url || detail?.id_card_signed_id) ? (
                  <div className="mt-2">
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      onClick={() => onRemoveIdCard(index)}
                      disabled={uploadingIdCard}
                    >
                      Delete
                    </Button>
                  </div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
          <Button type="button" color="primary" className="mt-2" style={{ color: '#fff' }} disabled={removed || saving || uploadingIdCard || !onSave} onClick={onSave}>{saving ? 'Saving...' : 'Save User / KTP'}</Button>
          <small className="d-block mt-1">Simpan setelah upload KTP. Tombol ini menyimpan form User yang sedang diisi.</small>
        </div>

        <hr />

        {/* Detail Address */}
        <h5 className="mb-2">Detail Address (saved on user_details)</h5>
        <Row form>
          <Col md="6">
            <FormGroup>
              <Label>Complete Address</Label>
              <Input
                disabled={removed}
                value={detail.complete_address || ''}
                onChange={(e) => set('complete_address', e.target.value)}
                placeholder="Street, number, etc."
              />
            </FormGroup>
          </Col>

          <Col md="6">
            <FormGroup>
              <Label>City Name</Label>
              <Input
                disabled={removed}
                value={detail.city_name || ''}
                onChange={(e) => set('city_name', e.target.value)}
                placeholder="Jakarta"
              />
            </FormGroup>
          </Col>

          <Col md="12">
            <FormGroup>
              <Label>Instruction</Label>
              <Input
                disabled={removed}
                value={detail.instruction || ''}
                onChange={(e) => set('instruction', e.target.value)}
                placeholder="Patokan, etc."
              />
            </FormGroup>
          </Col>
        </Row>

        <hr />

        <Row form>
          <Col md="6">
            <FormGroup>
              <Label>Instagram</Label>
              <Input
                disabled={removed}
                value={detail.instagram_account || ''}
                onChange={(e) => set('instagram_account', e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md="6">
            <FormGroup>
              <Label>Lead Source</Label>
              <Input
                disabled={removed}
                value={detail.lead_source || ''}
                onChange={(e) => set('lead_source', e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md="12">
            <FormGroup>
              <Label>Notes</Label>
              <Input
                type="textarea"
                disabled={removed}
                value={detail.notes || ''}
                onChange={(e) => set('notes', e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
        { !removed && bankVerification }
      </CardBody>
    </Card>
  )
}

SalesUsersDetailForm.propTypes = {
  onSave: PropTypes.func,
  saving: PropTypes.bool,
  detail: PropTypes.object.isRequired,
  bankVerification: PropTypes.node,
  index: PropTypes.number.isRequired,
  displayNumber: PropTypes.number.isRequired,
  canRemove: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onMakePrimary: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onUndoRemove: PropTypes.func.isRequired,

  // ✅ upload props
  uploadingIdCard: PropTypes.bool,
  onUploadIdCard: PropTypes.func.isRequired,
  onRemoveIdCard: PropTypes.func.isRequired,
}

export default SalesUsersDetailForm
